import { Button, Tag, Typography } from "antd";
import { CrownOutlined, FieldTimeOutlined, PlayCircleOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { type ReactNode } from "react";

import { type PageConfiguration } from "@fitness-app/data-models/entities/CheckoutVariant";

type PricingPlan = NonNullable<PageConfiguration["pricing"]>[number];
type VisualSection = "general" | "timer" | "pricing" | "vipPricing" | "guarantee" | "promoVideo";

interface ProductCheckoutVariantVisualEditorProps {
  activeSection: VisualSection;
  config: PageConfiguration;
  isOpeningPreview?: boolean;
  onOpenPreview: () => void;
  onSelectSection: (section: VisualSection) => void;
}

const sectionLabels: Record<VisualSection, string> = {
  general: "Produkt",
  timer: "Hero / timer",
  pricing: "Cennik",
  vipPricing: "VIP",
  guarantee: "Gwarancja",
  promoVideo: "Video",
};

const SectionIcon = ({ section }: { section: VisualSection }) => {
  if (section === "timer") return <FieldTimeOutlined />;
  if (section === "vipPricing") return <CrownOutlined />;
  if (section === "guarantee") return <SafetyCertificateOutlined />;
  if (section === "promoVideo") return <PlayCircleOutlined />;
  return <span className="text-xs font-bold">{section === "general" ? "P" : "C"}</span>;
};

const getPlansLabel = (plans?: PricingPlan[]) => {
  if (!plans?.length) {
    return "brak planów";
  }

  return `${plans.length} ${plans.length === 1 ? "plan" : "plany"}`;
};

const getSectionSummary = (section: VisualSection, config: PageConfiguration) => {
  if (section === "general") {
    return config.productNameOverride || "Domyślne dane produktu";
  }

  if (section === "timer") {
    if (!config.timer) return "Domyślny hero";
    return config.timer.title || "Timer włączony";
  }

  if (section === "pricing") {
    return config.pricingDisabled ? "Ukryty" : getPlansLabel(config.pricing);
  }

  if (section === "vipPricing") {
    return getPlansLabel(config.vipPricing);
  }

  if (section === "guarantee") {
    return config.guaranteeBanner?.title || "Wyłączona";
  }

  return config.promoVideo?.videoId ? config.promoVideo.title || config.promoVideo.videoId : "Wyłączone";
};

const getSectionState = (section: VisualSection, config: PageConfiguration) => {
  if (section === "pricing") return config.pricingDisabled ? "off" : "on";
  if (section === "timer") return config.timer ? "on" : "off";
  if (section === "vipPricing") return config.vipPricing?.length ? "on" : "off";
  if (section === "guarantee") return config.guaranteeBanner?.title ? "on" : "off";
  if (section === "promoVideo") return config.promoVideo?.videoId ? "on" : "off";
  return "on";
};

const SectionButton = ({
  active,
  children,
  config,
  section,
  onSelect,
}: {
  active: boolean;
  children: ReactNode;
  config: PageConfiguration;
  section: VisualSection;
  onSelect: () => void;
}) => {
  const state = getSectionState(section, config);

  return (
    <button
      type="button"
      className={`flex min-w-[180px] flex-1 items-center gap-3 rounded-lg border px-3 py-3 text-left transition ${
        active ? "border-[#a18d6d] bg-[#fbf8f2]" : "border-zinc-200 bg-white hover:border-zinc-300"
      }`}
      onClick={onSelect}
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
          active ? "bg-[#a18d6d] text-white" : "bg-zinc-100 text-zinc-500"
        }`}
      >
        <SectionIcon section={section} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-zinc-950">{children}</span>
        <span className="block truncate text-xs text-zinc-500">{getSectionSummary(section, config)}</span>
      </span>
      <Tag color={state === "on" ? "green" : "default"} className="m-0 shrink-0">
        {state === "on" ? "on" : "off"}
      </Tag>
    </button>
  );
};

const ActiveSectionDetails = ({ section, config }: { config: PageConfiguration; section: VisualSection }) => {
  if (section === "pricing") {
    return (
      <>
        <InfoRow label="Status" value={config.pricingDisabled ? "Ukryty" : "Widoczny"} />
        <InfoRow label="Plany" value={getPlansLabel(config.pricing)} />
        <InfoRow label="Karty" value={config.richPriceCards ? "Rozbudowane" : "Standardowe"} />
      </>
    );
  }

  if (section === "vipPricing") {
    return <InfoRow label="Plany VIP" value={getPlansLabel(config.vipPricing)} />;
  }

  if (section === "timer") {
    return (
      <>
        <InfoRow label="Hero" value={config.hideHero ? "Ukryte" : "Widoczne"} />
        <InfoRow label="Tryb" value={config.timer ? "Timer" : "Domyślny hero"} />
        {config.timer ? (
          <>
            <InfoRow label="Licznik" value={config.timer.hideCountdown ? "Ukryty" : "Widoczny"} />
            <InfoRow label="Miejsca" value={config.timer.showSeatsLeft ? "Widoczne" : "Ukryte"} />
          </>
        ) : null}
      </>
    );
  }

  if (section === "guarantee") {
    return (
      <>
        <InfoRow label="Tytuł" value={config.guaranteeBanner?.title || "Brak"} />
        <InfoRow label="Benefity" value={`${config.guaranteeBanner?.benefits?.length ?? 0}`} />
      </>
    );
  }

  if (section === "promoVideo") {
    return (
      <>
        <InfoRow label="Video ID" value={config.promoVideo?.videoId || "Brak"} />
        <InfoRow label="Pozycja" value={config.videoOnTop ? "Nad hero" : "Niżej na stronie"} />
      </>
    );
  }

  return (
    <>
      <InfoRow label="Nazwa" value={config.productNameOverride || "Domyślna"} />
      <InfoRow label="Obrazek" value={config.productImageOverride ? "Nadpisany" : "Domyślny"} />
      <InfoRow
        label="Szablon"
        value={
          config.landingTemplate === "hollywoodBlackFriday"
            ? "Black Friday"
            : config.landingTemplate === "hollywood2026"
              ? "Hollywood 2026"
              : "Klasyczny"
        }
      />
    </>
  );
};

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between gap-3 border-b border-zinc-100 py-2 last:border-b-0">
    <span className="text-xs font-semibold uppercase text-zinc-500">{label}</span>
    <span className="min-w-0 truncate text-right text-sm font-medium text-zinc-900">{value}</span>
  </div>
);

export const ProductCheckoutVariantVisualEditor = ({
  activeSection,
  config,
  isOpeningPreview,
  onOpenPreview,
  onSelectSection,
}: ProductCheckoutVariantVisualEditorProps) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-3">
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <div>
        <Typography.Text strong className="block text-base">
          Szybka edycja
        </Typography.Text>
        <Typography.Text type="secondary" className="text-xs">
          Wybierz obszar konfiguracji. Pełny podgląd otwiera edytowalne preview w nowej karcie.
        </Typography.Text>
      </div>
      <Button size="small" loading={isOpeningPreview} onClick={onOpenPreview}>
        Live preview
      </Button>
    </div>

    <div className="flex gap-2 overflow-x-auto pb-1">
      {(Object.keys(sectionLabels) as VisualSection[]).map((section) => (
        <SectionButton
          key={section}
          active={activeSection === section}
          config={config}
          section={section}
          onSelect={() => onSelectSection(section)}
        >
          {sectionLabels[section]}
        </SectionButton>
      ))}
    </div>

    <div className="mt-3 grid gap-2 rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2 sm:grid-cols-3">
      <ActiveSectionDetails section={activeSection} config={config} />
    </div>
  </div>
);
